/**
 * Элемент, в котором звучит собеседник, и право этому элементу играть звук.
 *
 * <p>Safari разрешает воспроизведение только из пользовательского жеста, а голос собеседника
 * приходит позже: у отвечающего — через секунду после нажатия, у звонящего — когда на том конце
 * возьмут трубку. Позвать у комнаты `startAudio()` изнутри жеста невозможно: комнаты ещё нет.
 *
 * <p>Прежний приём проигрывал тишину в отдельном элементе и не работал там, где нужен больше всего.
 * Прод за 45 дней (16.09.2026): на iPhone Safari даёт право играть не странице, а конкретному
 * элементу — отвечающие с прогревом упирались в блокировку не реже, чем без него (5 из 18 после
 * против 3 из 23 до). А у звонящих прогрева не было вовсе, и разрешение от нажатия «Позвонить»
 * истекало за несколько секунд ожидания: без блокировки ответ приходил в среднем за 2 секунды, с
 * блокировкой — за 7–9. Итог — 21 из 49 звонивших с iPhone и 8 из 28 с Mac не слышали собеседника,
 * пока не находили баннер.
 *
 * <p>Поэтому элемент один на всю страницу и живёт дольше любого звонка: жест разблокирует именно
 * его, разблокировка на нём остаётся, и голос приходит в него же. LiveKit на `detach` только
 * очищает `srcObject`, так что один и тот же элемент переживает сколько угодно разговоров.
 * Баннер «включить звук» остаётся страховкой.
 */

const SILENCE_SECONDS = 0.1;
const SILENCE_SAMPLE_RATE = 8000;
const WAV_HEADER_BYTES = 44;

const writeTag = (view: DataView, offset: number, tag: string) => {
  for (const [index, char] of [...tag].entries()) {
    view.setUint8(offset + index, char.charCodeAt(0));
  }
};

/**
 * Тишина, которой нажатие разблокирует элемент: wav, 16 бит, моно, 0,1 с при 8 кГц.
 *
 * <p>Длина важна. Прежняя константа несла один сэмпл: Safari на Mac (проверка 17.09.2026) такой
 * файл не декодирует, ставит элементу MEDIA_ERR_DECODE, и голос собеседника в этом элементе не
 * звучал вовсе. WebKit из Playwright на Linux декодирует и один сэмпл, так что ловит это только
 * настоящий Safari.
 */
const buildSilence = (): string => {
  const dataBytes = Math.round(SILENCE_SECONDS * SILENCE_SAMPLE_RATE) * 2;
  const view = new DataView(new ArrayBuffer(WAV_HEADER_BYTES + dataBytes));
  writeTag(view, 0, 'RIFF');
  view.setUint32(4, WAV_HEADER_BYTES - 8 + dataBytes, true);
  writeTag(view, 8, 'WAVE');
  writeTag(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // моно
  view.setUint32(24, SILENCE_SAMPLE_RATE, true);
  view.setUint32(28, SILENCE_SAMPLE_RATE * 2, true); // байт в секунду
  view.setUint16(32, 2, true); // байт на сэмпл
  view.setUint16(34, 16, true); // бит на сэмпл
  writeTag(view, 36, 'data');
  view.setUint32(40, dataBytes, true);
  const bytes = String.fromCharCode(...new Uint8Array(view.buffer));
  return `data:audio/wav;base64,${btoa(bytes)}`;
};

let silence: string | null = null;
let sink: HTMLAudioElement | null = null;

/**
 * Скрытый элемент для голоса собеседника. В DOM обязательно: отсоединённый элемент часть браузеров
 * не озвучивает.
 *
 * <p>Детей у элемента нет, и пустую `<track kind="captions">` сюда не добавлять. Safari (Mac, 26.3.1,
 * проверка 17.09.2026) ждёт, пока загрузится дорожка без источника, держит `readyState` на 2, и
 * `play()` не завершается никогда: ни тишина, ни голос не звучат. Субтитров у живого WebRTC всё
 * равно нет.
 *
 * <p>Элемент в ошибке заменяется свежим: Safari не играет в нём и поток, который отдают потом.
 */
export const getCallAudioSink = (): HTMLAudioElement => {
  if (sink?.error) {
    sink.remove();
    sink = null;
  }
  if (!sink || !sink.isConnected) {
    const element = document.createElement('audio');
    element.autoplay = true;
    element.setAttribute('playsinline', '');
    element.setAttribute('aria-hidden', 'true');
    element.style.display = 'none';
    document.body.appendChild(element);
    sink = element;
  }
  return sink;
};

/**
 * Элемент, в который подключают голос собеседника. Тишина из него снимается: голос приходит
 * раньше, чем она доиграет, и без этого после разговора, когда LiveKit очистит `srcObject`,
 * элемент вернулся бы к ней. Снятие атрибута `src` загрузку не перезапускает.
 */
export const takeCallAudioSink = (): HTMLAudioElement => {
  const element = getCallAudioSink();
  element.removeAttribute('src');
  return element;
};

/**
 * Разблокировать элемент голоса. Вызывать внутри обработчика нажатия, до любых `await`.
 *
 * <p>Отказ здесь ничего не ломает и не логируется: браузер имеет полное право не разрешить, и
 * тогда останется баннер «включить звук» в окне разговора.
 */
export const primeAudioPlayback = (): void => {
  if (typeof document === 'undefined') {
    return;
  }
  try {
    const element = getCallAudioSink();
    // Идёт разговор — поток уже в элементе, трогать его нельзя: достаточно play(). Свободен —
    // проигрываем тишину, иначе play() нечего играть и разблокировка не наступит.
    if (!element.srcObject) {
      silence ??= buildSilence();
      element.src = silence;
    }
    void element.play().catch(() => undefined);
  } catch {
    // Не разрешили — останется баннер «включить звук» в окне разговора.
  }
};
